/**
 * @module: 猎职位相关
 * @author: Hualing Liu
 * @date: 2020-06-03 17:39:00
 */

import { observable, action, flow } from 'mobx';
import { Toast } from 'antd-mobile';
import { ajax } from '@clouderwork/ywk';
import { IResumeDetail, IEmployments, IPortfolios, ICandidateList, IZhiwei } from '@src/entity/zhaopin/recommend';
import { withRole } from '@src/utils/residentNew';
import { resumeStatusConfig } from '@src/utils/mapping';

// 猎职位列表
interface IRecommendParams {
  pagenum: number; // 页码
  pagesize: number; // 每页条数
  keyword?: string; //关键字
  status?: string | number;
  role: number; // 当前调用所使用的角色(person,company)
  user?: string;
  dtype?: number;
  exper_year?: number; // 工作年限
  degree?: number; //学历过滤
  apply_at?: number; // 应聘时间
}
// 投递映射
// 100	筛选阶段
// 120	笔试阶段
// 150	面试阶段
// 600	offer阶段 待录用
// 610	offer阶段 待确认
// 620	待入职阶段
// 650	已入职阶段
// 900	失败/放弃阶段
// 1000	失败/放弃阶段

interface IEchartData {
  // 候选人接口类型定义
  dates: string[]; // 日期轴线
  acitve: number[]; // 当日推荐激活数
  interview: number[]; // 当日受邀面试
  entry: number[]; // 当日推荐入职数
}

class Recommend {
  // 猎职位概览
  @observable echartData: any;
  // 猎职位概览统计
  @observable statisticOffer:
    | {
        un_pick: number;
        un_exam: number;
        un_invite: number;
        un_join: number;
        un_result: number;
        un_offer: number;
      }
    | undefined;
  // 猎职位概览待入职简历
  // @observable swiperOffer: any;
  // 猎职位列表
  @observable huntJobList: any;

  // 接口请求状态
  @observable sendState: string = 'init';
  // 简历详情
  @observable resumeDetail?: IResumeDetail;
  // 我的简历详情
  @observable mineDetail?: IResumeDetail;
  // 招人才工作经历数组
  @observable employments?: IEmployments;
  // 招人才 项目经历数组
  @observable portfolios?: IPortfolios;
  // 候选人学历要求Id
  @observable eduExperId: number | null = null;
  // 候选人工作经验Id
  @observable workExperId: number | null = null;
  // 候选人应聘时间Id
  @observable applyId: number | null = null;
  // 我的候选人列表
  @observable candidateList: ICandidateList[] = [];
  // 我的候选人筛选弹窗
  @observable candidateModal: boolean = false;
  // 职位切换弹窗
  @observable toggleModal: boolean = false;
  @observable offers: IZhiwei[] = [];
  // 职能名字
  @observable zhinengName: string = '';
  // 职位名字
  @observable zhiweiName: string = '';
  // 职位uuid
  @observable zhiweiUuid: string = '';
  @observable pullUp: boolean = false;
  // 招人才tab值
  @observable resumesTab: string = 't1';

  @observable candidateFilter: {
    eduExperId: number | null;
    workExperId: number | null;
    applyId: number | null;
  } = {
    eduExperId: null,
    workExperId: null,
    applyId: null
  };

  @action.bound
  setResumesTab = (val: string) => {
    // 招人才tab值切换
    this.resumesTab = val;
  };
  @action.bound
  setSendState = (sendState: string) => {
    // 设置接口请求状态
    this.sendState = sendState;
  };

  // 猎职位概览柱形图
  @action.bound
  getOverviewEchart = flow(function* (this: Recommend, params: any) {
    try {
      const res: any = yield ajax.post('/zhaopin/api/recommend/overview', params);
      if (res.error_code === 0) {
        const { result = {} } = res;
        this.echartData = result;
      }
    } catch (error) {
      return Promise.reject('请求错误');
    }
  });

  // 猎职位概览简历统计
  @action.bound
  getHuntstatistics = flow(function* (this: Recommend, params: any) {
    try {
      const res: any = yield ajax.post('/zhaopin/api/delivery/todo', { hunt: true, ...params });
      if (res.error_code === 0) {
        const { result = {} } = res;
        this.statisticOffer = result;
      }
    } catch (error) {
      return Promise.reject('请求错误');
    }
  });

  // 猎职位概览轮播数据-待入职简历
  //  @action.bound
  //  getHuntWaitEntry = flow(function * (this:Recommend, params: any) {
  //    try {
  //      const res:any = yield ajax.getJson('/zhaopin/api/delivery', params)
  //      if (res.error_code === 0) {
  //        const { delivery = [] } = res;
  //        this.swiperOffer = delivery;
  //      }
  //    } catch (error) {
  //      return Promise.reject('请求错误')
  //    }
  //  })

  /**
   * @param {boolean} isPull 是否上拉加载
   */
  @action.bound
  getHuntList = flow(function* (this: Recommend, params: IRecommendParams, isPullOrPush: boolean = false) {
    if (!isPullOrPush) {
      this.sendState = 'pending';
    }

    // 获取候选人列表
    try {
      const res: any = yield ajax.getJson('/zhaopin/api/delivery', params);
      this.sendState = 'done';
      if (res.error_code === 0) {
        const data = res.delivery.map(cur => {
          let value = '';
          if (cur.status) {
            const keys = Object.keys(resumeStatusConfig);
            let key = keys.find(key => key.includes(cur.status.toString()));
            if (key) value = resumeStatusConfig[key].value;
          }
          const resume = cur.resume;
          return {
            ...cur,
            avatar: resume.avatar,
            real_name: resume.real_name,
            gender: resume.gender_text,
            work_city: resume.work_city,
            start_work_text: resume.start_work_text,
            exper_year_text: resume.exper_year_text,
            age: resume.age,
            salary: resume.salary,
            skills: resume.skills,
            last_edu: resume.last_edu,
            job_status: resume.job_status,
            uuid: resume.uuid,
            deliverUuid: cur.uuid,
            deliverOffer: cur.offer,
            status: status
              ? resumeStatusConfig && resumeStatusConfig[cur.status] && resumeStatusConfig[cur.status].value
              : value, // 候选人statusNum阶段映射
            statusNum: cur.status // 候选人statusNum
          };
        });
        if (res.total < params.pagenum * 10) {
          // 不可以继续翻页
          this.pullUp = true;
        } else {
          this.pullUp = false;
        }
        if (params.pagenum === 1) {
          this.huntJobList = data;
          return Promise.reject('刷新成功');
        } else {
          this.huntJobList = this.huntJobList.concat(data);
        }
      } else {
        return Promise.reject('请求失败');
      }
    } catch (err) {
      this.sendState = 'error';
      return Promise.reject('请求失败');
    }
  });

  /**
   * @param hide_info 1 显示 2 隐藏
   */
  @action.bound
  getResumeDetail = flow(function* (this: Recommend, uuid: string, hide_info = 2) {
    // 获取简历详情
    console.log('/api/resumes/detail====', 111);
    this.sendState = 'pending';
    try {
      const res: any = yield ajax.getJson(
        `/zhaopin/api/resumes/detail/${uuid}`,
        withRole({
          hide_info
        })
      );
      this.sendState = 'done';
      if (res.error_code === 0) {
        this.resumeDetail = res.resume;
      } else {
        Toast.fail(res.message, 1);
      }
    } catch (error) {
      this.sendState = 'error';
    }
  });

  @action.bound
  getMineDetail = flow(function* (this: Recommend) {
    // 获取我的简历详情
    this.sendState = 'pending';
    try {
      const res: any = yield ajax.getJson('/zhaopin/api/resumes/mine', withRole({}, 2));
      this.sendState = 'done';
      if (res.error_code === 0) {
        this.mineDetail = res.resume;
      }
    } catch (error) {
      this.sendState = 'error';
    }
  });

  @action.bound
  getOfferName = flow(function* (this: Recommend, name: string, autoSetZhiwei?: boolean) {
    // 获取职位
    try {
      const res: any = yield ajax.getJson(
        '/zhaopin/api/delivery/offers',
        withRole({
          name: name,
          status: 1,
          order_by: 'create_at',
          pagenum: 1,
          pagesize: 999
        })
      );
      if (res.error_code === 0) {
        const offers = res.offers;
        this.offers = res.offers;
        if (autoSetZhiwei && this.zhinengName === '' && this.zhiweiUuid === '' && this.zhiweiName === '') {
          this.zhinengName = (offers[0] && offers[0].job_title) || this.zhinengName;
          this.zhiweiUuid = (offers[0] && offers[0].uuid) || this.zhiweiUuid;
          this.zhiweiName = (offers[0] && offers[0].name) || this.zhiweiName;
        }
      } else {
        // Toast.fail(res.message, 1);
      }
    } catch (error) {
      console.log(error);
    }
  });

  @action.bound
  setPortfolios = (portfolios: IPortfolios) => {
    // 存储项目经历
    this.portfolios = portfolios;
  };

  @action.bound
  setEmployments = (employments: IEmployments) => {
    // 存储工作经历
    this.employments = employments;
  };

  @action.bound
  setExper = (id: number, name: string) => {
    // 如果里面该id存在教育经历中这从教育经历中移除该ID，否则则往教育经历中加入该ID
    this[name] = id;
  };

  @action.bound
  setCandidateModal = (val: boolean) => {
    // 控制候选人筛选弹窗显示隐藏
    this.candidateModal = val;
  };

  @action.bound
  setToggleModal = (val: boolean) => {
    // 控制职位切换弹窗显示隐藏
    this.toggleModal = val;
  };

  @action.bound
  setZhiweiName = (val: string, uuid: string, zhinengName: string) => {
    // 存储选中的值
    this.zhiweiName = val;
    this.zhiweiUuid = uuid;
    this.zhinengName = zhinengName;
    console.log('this.zhinengName', this.zhinengName);
  };
}

export default Recommend;
